import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import axios from 'axios';
import { Eye, EyeOff, Loader2, AlertCircle } from 'lucide-react';

import { GoogleSignIn } from '@/components/carnet/GoogleSignIn';
import { BrandMark } from '@/components/layout/BrandMark';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { googleLoginRequest, loginRequest } from '@/services/api/auth.api';
import { getApiErrorMessage } from '@/services/api/client';
import { useAuthStore } from '@/store/authStore';

const schema = z.object({
  username: z.string().min(2, 'Mínimo 2 caracteres'),
  password: z.string().min(4, 'Mínimo 4 caracteres'),
});

type FormValues = z.infer<typeof schema>;

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation() as { state?: { from?: { pathname?: string } } };
  const [searchParams] = useSearchParams();
  const { setAuth, isAuthenticated } = useAuthStore();
  const [showPwd, setShowPwd] = useState(false);

  const from = location.state?.from?.pathname ?? '/';
  const expired = searchParams.get('expired') === '1';

  // Si ya hay sesión, brincar directo al home (caso recarga manual de /login)
  useEffect(() => {
    if (isAuthenticated) navigate(from, { replace: true });
  }, [isAuthenticated, from, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { username: '', password: '' },
  });

  const mutation = useMutation({
    mutationFn: (data: FormValues) => loginRequest(data.username, data.password),
    onSuccess: (resp) => {
      setAuth(resp.access_token, {
        id: resp.user.id,
        username: resp.user.username,
        // El backend aún no devuelve email en /auth/login — placeholder por compat.
        email: null,
        rol: resp.user.rol,
      });
      navigate(from, { replace: true });
    },
  });

  // Google sign-in (POST /auth/google): same session and navigation as the password flow.
  const googleMutation = useMutation({
    mutationFn: (credential: string) => googleLoginRequest(credential),
    onSuccess: (resp) => {
      setAuth(resp.access_token, {
        id: resp.user.id,
        username: resp.user.username,
        email: resp.user.email ?? null,
        rol: resp.user.rol,
      });
      navigate(from, { replace: true });
    },
  });

  return (
    <div className="min-h-screen flex">
      {/* ─── Panel izquierdo: brand / visual ─────────────────────────────── */}
      <div className="hidden lg:flex lg:flex-1 relative overflow-hidden bg-gradient-to-br from-primary-700 via-primary-800 to-primary-950">
        {/* Mesh decorativo */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-primary-400 blur-3xl" />
          <div className="absolute top-1/2 -right-32 h-[28rem] w-[28rem] rounded-full bg-primary-300 blur-3xl opacity-50" />
          <div className="absolute -bottom-32 left-1/3 h-80 w-80 rounded-full bg-primary-500 blur-3xl opacity-40" />
        </div>

        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />

        <div className="relative flex flex-col justify-between p-12 text-white w-full max-w-2xl">
          <div className="flex items-center gap-3">
            <BrandMark className="h-12 w-12 ring-1 ring-white/20" />
            <div className="leading-tight">
              <p className="font-bold text-xl tracking-tight">Master Balance</p>
              <p className="text-xs uppercase tracking-widest text-white/60">Panel de Gestión</p>
            </div>
          </div>

          <div className="space-y-6">
            <h2 className="text-4xl font-bold leading-tight tracking-tight">
              Control de acceso, inventario y mantenimientos en una sola plataforma.
            </h2>
            <p className="text-lg text-white/70 leading-relaxed max-w-md">
              Gestiona el ciclo de vida completo de los equipos de la organización con
              sincronización en tiempo real desde Google&nbsp;Workspace y GLPI.
            </p>

            <div className="grid grid-cols-3 gap-4 pt-4 max-w-md">
              <Stat label="Colaboradores" value="300+" />
              <Stat label="Activos" value="500+" />
              <Stat label="Sincronización" value="24/7" />
            </div>
          </div>

          <p className="text-xs text-white/40">© {new Date().getFullYear()} GSH · Todos los derechos reservados</p>
        </div>
      </div>

      {/* ─── Panel derecho: formulario ──────────────────────────────────── */}
      <div className="flex flex-1 items-center justify-center px-6 py-12 bg-background">
        <div className="w-full max-w-md space-y-8">
          {/* Logo mobile-only */}
          <div className="lg:hidden flex items-center gap-3 justify-center">
            <BrandMark className="h-10 w-10 ring-1 ring-border" />
            <p className="font-bold text-lg tracking-tight text-foreground">Master Balance</p>
          </div>

          <div className="space-y-2 text-center lg:text-left">
            <h1 className="text-2xl lg:text-3xl font-bold tracking-tight text-foreground">
              Bienvenido de nuevo
            </h1>
            <p className="text-sm text-muted-foreground">
              Ingresa tus credenciales para acceder al panel.
            </p>
          </div>

          {expired && (
            <Banner kind="warn">
              Tu sesión expiró por inactividad. Por favor ingresa de nuevo.
            </Banner>
          )}

          {mutation.isError && (
            <Banner kind="error">
              {getApiErrorMessage(mutation.error, 'Credenciales inválidas')}
            </Banner>
          )}

          <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="username">Usuario</Label>
              <Input
                id="username"
                autoComplete="username"
                autoFocus
                placeholder="tu.usuario"
                {...register('username')}
                aria-invalid={!!errors.username}
              />
              {errors.username && (
                <p className="text-xs text-destructive">{errors.username.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Contraseña</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPwd ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="pr-10"
                  {...register('password')}
                  aria-invalid={!!errors.password}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPwd ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-xs text-destructive">{errors.password.message}</p>
              )}
            </div>

            <Button type="submit" size="lg" className="w-full" disabled={mutation.isPending}>
              {mutation.isPending ? (
                <>
                  <Loader2 className="animate-spin" />
                  Iniciando sesión...
                </>
              ) : (
                'Iniciar sesión'
              )}
            </Button>
          </form>

          <div className="space-y-4">
            <div className="relative" aria-hidden="true">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-background px-3 text-xs uppercase tracking-wide text-muted-foreground">
                  o
                </span>
              </div>
            </div>

            <GoogleSignIn
              variant="light"
              onCredencial={(credential) => googleMutation.mutate(credential)}
              deshabilitado={mutation.isPending || googleMutation.isPending}
            />

            {googleMutation.isError && (
              <Banner kind="error">{googleLoginErrorMessage(googleMutation.error)}</Banner>
            )}
          </div>

          <p className="text-center text-xs text-muted-foreground">
            ¿Problemas para acceder? Contacta al área de soporte TI.
          </p>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-l-2 border-white/20 pl-3">
      <p className="text-2xl font-bold tabular">{value}</p>
      <p className="text-xs text-white/60 uppercase tracking-wide">{label}</p>
    </div>
  );
}

function Banner({ kind, children }: { kind: 'warn' | 'error'; children: React.ReactNode }) {
  const styles =
    kind === 'error'
      ? 'border-destructive/30 bg-destructive/5 text-destructive'
      : 'border-amber-300/50 bg-amber-50 text-amber-800';
  return (
    <div className={`flex items-start gap-2 rounded-md border px-3 py-2.5 text-sm ${styles}`}>
      <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
      <span>{children}</span>
    </div>
  );
}

/** /auth/google answers 403 when the Google account has no enabled panel user. */
function googleLoginErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.response?.status === 403) {
      return getApiErrorMessage(
        err,
        'Tu cuenta de Google no está habilitada en el panel. Contacta a un administrador.',
      );
    }
    if (!err.response) {
      return 'Sin conexión con el servidor. Revisa tu conexión e inténtalo de nuevo.';
    }
  }
  return getApiErrorMessage(err, 'No pudimos iniciar sesión con Google. Inténtalo de nuevo.');
}
